import { formatDate } from '@amelio/ui-library';

import type { KeyResultUnitValue } from '@/constants/KeyResultUnit';

import { KeyResultUnit } from '@/constants/KeyResultUnit';
import { Locale } from '@/constants/Locale';
import { formatPercent, formatQuantity } from '@/utils/numberFormat';

/**
 * A day written out in full, as `formatDate` does, with the French ordinal on the first of the
 * month: "1er janvier 2026", never "1 janvier 2026".
 */
export const formatLongDate = (date: string, language: string): string => {
    const formatted = formatDate({ date, language });

    return language.startsWith(Locale.FR) ? formatted.replace(/^1 /, '1er ') : formatted;
};

export const DEFAULT_CURRENCY_SYMBOL = '$';

/**
 * `24` with `'$'` -> `'$24'` in English, or `'24 $'` in every other language, where the symbol
 * follows the amount after a non-breaking space.
 *
 * Where the symbol sits is a separate question from how the number itself is written, and this
 * rule is the one the module has always applied: English on one side, everything else on the
 * other.
 */
export const formatMoney = (value: number, currencySymbol: string, language: string): string => {
    const amount = formatQuantity(value, language);

    return language === Locale.EN
        ? `${currencySymbol}${amount}`
        : `${amount}\u00a0${currencySymbol}`;
};

/**
 * One amount of a key result, worded on its own in the row's unit: the raw number for `Number`
 * and `YesNo`, `50 %` for a percentage, `50 $` for an amount. Used for the denominator of the
 * value pill (the form's key-result rows and the detail page's measurement cards alike, so the
 * same target never reads as a bare number on one page and a formatted one on the other) and for
 * the value the detail page's progress bar previews on hover.
 */
export const formatKeyResultTarget = (
    targetValue: number,
    unit: KeyResultUnitValue | undefined,
    currencySymbol: string | undefined,
    language: string,
): string => {
    if (unit === KeyResultUnit.PERCENTAGE) {
        return formatPercent(targetValue, language);
    }

    if (unit === KeyResultUnit.MONETARY) {
        // The module's one money formatter: symbol after the amount in French, before it in
        // English, exactly as the discussion journal quotes a value.
        return formatMoney(targetValue, currencySymbol || DEFAULT_CURRENCY_SYMBOL, language);
    }

    return formatQuantity(targetValue, language);
};

interface FormatKeyResultValueOptions {
    value: number;
    unit: KeyResultUnitValue;
    /** Falls back to the module's default symbol when a monetary key result carries none. */
    currencySymbol: string | null | undefined;
    /** Quoted after the value for counted and monetary units ("24 / 50"), like the prototype. */
    targetValue: number;
    language: string;
    /** The translated labels a yes/no key result reads as. */
    yesNoLabels: { yes: string; no: string };
}

/**
 * One value of a key result, worded the way the reader sees it on the measurement itself:
 * `OUI`/`NON` for a yes/no, `35 %` for a percentage, `24 / 50` for a count, `24 $ / 50 $` for
 * an amount. Used wherever a value is quoted in prose (the discussion's automatic entries).
 */
export const formatKeyResultValue = ({
    value,
    unit,
    currencySymbol,
    targetValue,
    language,
    yesNoLabels,
}: FormatKeyResultValueOptions): string => {
    switch (unit) {
        case KeyResultUnit.YES_NO:
            return value > 0 ? yesNoLabels.yes : yesNoLabels.no;
        case KeyResultUnit.PERCENTAGE:
            return formatPercent(value, language);
        case KeyResultUnit.MONETARY: {
            const symbol = currencySymbol || DEFAULT_CURRENCY_SYMBOL;
            return `${formatMoney(value, symbol, language)} / ${formatMoney(targetValue, symbol, language)}`;
        }
        default:
            return `${formatQuantity(value, language)} / ${formatQuantity(targetValue, language)}`;
    }
};
